// in src/MyLayout.js
import * as React from "react";
import { forwardRef } from "react";
import {
  AppBar,
  Menu,
  Layout,
  UserMenu,
  useResourceDefinitions,
  TitlePortal,
} from "react-admin";
import { Box, Button, Divider, ListSubheader, MenuItem } from "@mui/material";
import ExitIcon from "@mui/icons-material/PowerSettingsNew";
import AddIcon from "@mui/icons-material/AddToQueue";
import RouteIcon from "@mui/icons-material/AltRoute";
import HourglassIcon from "@mui/icons-material/HourglassEmpty";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import AdminTitle from "./AdminTitle";
import AgencyLogo from "./AgencyLogo";
import ViewModeToggle from "../ViewModeToggle";

const MyLogoutButton = forwardRef((props, ref) => {
  const { signout } = useAuth();
  const navigate = useNavigate();

  const handleClick = async () => {
    await signout();
    navigate("/login");
  };
  return (
    <MenuItem onClick={handleClick} ref={ref}>
      <ExitIcon /> Logout
    </MenuItem>
  );
});

const MyUserMenu = () => {
  return (
    <UserMenu>
      <MyLogoutButton />
    </UserMenu>
  );
};

const GarityAppBar = () => {
  return (
    <AppBar userMenu={<MyUserMenu />}>
      <TitlePortal />
      <Box flex="1" />
      <AdminTitle />
      <Box flex="1" />
      {/* #50 — principal agent/agency view toggle (renders only for principals
          linked to an agent record). */}
      <ViewModeToggle />
    </AppBar>
  );
};

const MyMenu = () => {
  const resources = useResourceDefinitions();
  const { user, isFullAdmin, isAgencyAdmin } = useAuth();

  // API Logs is a debug tool; render it at the very bottom under a "Debug"
  // header rather than mixed in with the day-to-day resources (#17).
  const primaryResources = Object.keys(resources).filter((r) => r !== "api-log");

  return (
    <Menu>
      <AgencyLogo />
      {primaryResources.map((resource) => (
        <Menu.ResourceItem key={resource} name={resource} />
      ))}
      {isFullAdmin(user) && (
        <Menu.Item
          to="/admin/agent/import"
          primaryText="Agent Import"
          leftIcon={<AddIcon />}
        />
      )}
      {isFullAdmin(user) && (
        <Menu.Item
          to="/admin/ace-routing"
          primaryText="ACE Routing"
          leftIcon={<RouteIcon />}
        />
      )}
      {isAgencyAdmin(user) && (
        <Menu.Item
          to="/admin/my-downline"
          primaryText="My Downline"
          leftIcon={<AccountTreeIcon />}
        />
      )}
      {isAgencyAdmin(user) && (
        <Menu.Item
          to="/admin/awaiting-countersignature"
          primaryText="Awaiting Countersignature"
          leftIcon={<HourglassIcon />}
          sx={{ whiteSpace: "normal", height: "auto", lineHeight: 1.2 }}
        />
      )}
      <Menu.Item
        to="/contract"
        target="_blank"
        primaryText={
          <Button variant="contained" fullWidth sx={{ whiteSpace: "nowrap" }}>
            New Contract Request
          </Button>
        }
      />
      {resources["api-log"] && (
        <>
          <Divider sx={{ my: 1 }} />
          <ListSubheader
            component="div"
            sx={{ bgcolor: "transparent", lineHeight: "2.2em", fontWeight: 700 }}
          >
            Debug
          </ListSubheader>
          <Menu.ResourceItem name="api-log" />
        </>
      )}
    </Menu>
  );
};

const MyLayout = (props) => (
  <Layout {...props} appBar={GarityAppBar} menu={MyMenu} />
);

export default MyLayout;
